import { ReportCard } from "@/components/reports/report-card";
import {
  DateRangeFields,
  ReportError,
  ReportLoading,
} from "@/components/reports/report-page";
import { Badge } from "@/components/ui/badge";
import { buildMetadata } from "@/lib/metadata";
import { ReportService } from "@/services/report-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { normalizeReportSearch, reportSearchSchema } from "./-report-search";

export const Route = createFileRoute("/_main/reports/")({
  validateSearch: reportSearchSchema,
  component: ReportsRoute,
  head: () => ({
    meta: buildMetadata({
      title: "Reports",
      description: "View supplier reports",
      keywords: ["reports", "orders", "products", "sales"],
    }),
  }),
});

function ReportsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = normalizeReportSearch(search);
  const hubQuery = useQuery({
    queryKey: ["reports", "hub", range.dateFrom, range.dateTo],
    queryFn: () => ReportService.getHub(range),
  });

  return (
    <main className="space-y-9 p-4 md:p-8">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-medium text-3xl tracking-tight">Reports</h1>
          {hubQuery.data ? (
            <Badge className="rounded-full border-0 bg-[#c9ff79] px-3 py-1.5 text-[#102d03]">
              {hubQuery.data.total_orders} orders
            </Badge>
          ) : null}
        </div>

        <DateRangeFields
          range={range}
          onRangeChange={(nextRange) => {
            navigate({ search: nextRange, replace: true });
          }}
        />
      </header>

      {hubQuery.isLoading ? <ReportLoading /> : null}
      {hubQuery.isError ? <ReportError /> : null}

      {hubQuery.data?.reports.length === 0 ? (
        <section className="rounded-xl border border-dashed p-10 text-center">
          <h2 className="font-medium">No reports are enabled</h2>
          <p className="mt-2 text-muted-foreground text-sm">
            Ask an OpenFarming administrator to enable reports for this
            supplier.
          </p>
        </section>
      ) : null}

      {hubQuery.data?.reports.length ? (
        <section className="grid gap-6 md:grid-cols-2 2xl:grid-cols-4">
          {hubQuery.data.reports.map((report) => (
            <ReportCard
              key={report.key}
              report={report}
              dateFrom={range.dateFrom}
              dateTo={range.dateTo}
            />
          ))}
        </section>
      ) : null}
    </main>
  );
}
